import { defineMessages, useIntl } from 'react-intl';
import { type DynamicYieldCampaign } from 'src/shared/types/dynamicYieldCampaign';
import yn from 'yn';

import {
    GA4EventName,
    GA4FeatureCategory,
    PromotionType,
    trackAnalyticsEvent,
    trackEecPromotionClick,
    trackEecPromotionImpression,
} from '@jsmdg/tracking';
import { Banner, Breakpoint, useBreakpoint, Wysiwyg } from '@jsmdg/yoshi';
import { CreativePlaceholderId } from '../../../../shared/enums/creativePlaceholderId';
import { CreativeName } from '../../../enums/trackingPromotions';
import { isGifImageUrl } from '../../../helper/isGifImageUrl';
import { useImpressionTracking } from '../../../hooks';
import { DyCampaignWrapper } from '../../DyCampaignWrapper/DyCampaignWrapper';
import { getDyCountdown, getDyPromo } from './getDyBannerBlocks';

type DyHeroStageBannerProps = {
    readonly creative: DynamicYieldCampaign;
    readonly hasNavigationCreative: boolean;
    readonly verticalPosition: number;
};

const mobileImageSize = { width: 575, height: 90 };
const desktopImageSize = { width: 1_220, height: 90 };
const bannerSizes = '100vw';

const messages = defineMessages({
    infoText: { defaultMessage: 'Kampagnenbedingungen' },
    countdownDays: { defaultMessage: 'T' },
    countdownHours: { defaultMessage: 'S' },
    countdownMinutes: { defaultMessage: 'M' },
    countdownSeconds: { defaultMessage: 'S' },
    copiedText: { defaultMessage: 'Kopiert!' },
});

const getModalInfo = (creative: DynamicYieldCampaign) => {
    const { asterisk_enabled, asterisk_label, asterisk_text, icon_color } = creative.data;

    return yn(asterisk_enabled)
        ? {
              headline: <Wysiwyg content={asterisk_label || ''} />,
              infoContent: <Wysiwyg content={asterisk_text || ''} />,
              infoIconColor: icon_color || '',
              a11yCloseText: asterisk_label || '',
          }
        : undefined;
};

const getCountdownUnits = (intl: ReturnType<typeof useIntl>) => ({
    days: intl.formatMessage(messages.countdownDays),
    hours: intl.formatMessage(messages.countdownHours),
    minutes: intl.formatMessage(messages.countdownMinutes),
    seconds: intl.formatMessage(messages.countdownSeconds),
});

const DyHeroStageBanner = ({
    creative,
    hasNavigationCreative,
    verticalPosition,
}: DyHeroStageBannerProps) => {
    const intl = useIntl();
    const isDesktop = useBreakpoint(Breakpoint.XS);

    const { campaign_name, desktop_image, image_alt, link, mobile_image, tracking_name } =
        creative.data;

    const adaptedVerticalPosition = hasNavigationCreative ? verticalPosition - 1 : verticalPosition;

    const position = `vertical${adaptedVerticalPosition}_horizontal1`;

    const placeholderId = isDesktop
        ? CreativePlaceholderId.HeroStageDesktop
        : CreativePlaceholderId.HeroStageMobile;

    const promotionTrackingPayload = {
        id: tracking_name ?? '',
        name: campaign_name ?? '',
        creative: placeholderId,
        position,
    };

    const promotionTrackingEventData = {
        creativeName: CreativeName.HeroBanner,
        creativeSlot: position,
        promotionName: campaign_name ?? '',
        promotionId: tracking_name ?? '',
        locationId: creative.data.link || '',
        promotionType: PromotionType.Dynamic,
        promotionPlaceholder: placeholderId,
    };

    const setMobileImpressionTrackedElement = useImpressionTracking(() =>
        trackEecPromotionImpression([promotionTrackingPayload], promotionTrackingEventData),
    );

    const setDesktopImpressionTrackedElement = useImpressionTracking(() =>
        trackEecPromotionImpression([promotionTrackingPayload], promotionTrackingEventData),
    );

    if (!mobile_image || !desktop_image) {
        return null;
    }

    const isGifMobileImage = isGifImageUrl(mobile_image);
    const isGifDesktopImage = isGifImageUrl(desktop_image);

    const { desktop: desktopCountdown, mobile: mobileCountdown } = getDyCountdown(
        creative.data,
        getCountdownUnits(intl),
    );
    const { desktop: desktopPromoCode, mobile: mobilePromoCode } = getDyPromo(
        creative.data,
        intl.formatMessage(messages.copiedText),
    );

    const onInfoClick = () => {
        trackAnalyticsEvent({
            eventData: {
                eventName: GA4EventName.ClickButton,
                feature_category: GA4FeatureCategory.PromotionDynamic,
                creative_name: CreativeName.HeroBanner,
                click_element: 'Info icon',
            },
        });
    };

    return (
        <DyCampaignWrapper analyticsMetadata={creative.analyticsMetadata}>
            <Banner
                imageUrl={mobile_image}
                alt={image_alt}
                link={link}
                modalInfos={getModalInfo(creative)}
                fullWidth
                sizes={bannerSizes}
                width={isGifMobileImage ? undefined : mobileImageSize.width}
                height={isGifMobileImage ? undefined : mobileImageSize.height}
                hasAspectRatio={false}
                lazyLoad
                className="d-xs-none mb-0-5x w-100"
                onClick={() => {
                    trackEecPromotionClick(promotionTrackingPayload, promotionTrackingEventData);
                }}
                onInfoClick={onInfoClick}
                a11yButtonText={intl.formatMessage(messages.infoText)}
                ref={setMobileImpressionTrackedElement}
                promoCode={mobilePromoCode}
                countdown={mobileCountdown}
            />
            <Banner
                imageUrl={desktop_image}
                alt={image_alt}
                link={link}
                modalInfos={getModalInfo(creative)}
                fullWidth
                sizes={bannerSizes}
                width={isGifDesktopImage ? undefined : desktopImageSize.width}
                height={isGifDesktopImage ? undefined : desktopImageSize.height}
                hasAspectRatio
                lazyLoad
                className="d-none d-xs-block mb-0-5x w-100"
                onClick={() => {
                    trackEecPromotionClick(promotionTrackingPayload, promotionTrackingEventData);
                }}
                onInfoClick={onInfoClick}
                a11yButtonText={intl.formatMessage(messages.infoText)}
                ref={setDesktopImpressionTrackedElement}
                promoCode={desktopPromoCode}
                countdown={desktopCountdown}
            />
        </DyCampaignWrapper>
    );
};

export { DyHeroStageBanner };
